import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Lock, Mail } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success("Connexion réussie");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="bg-card border shadow-lg">
          <CardHeader className="text-center space-y-4 pb-6">
            {/* Logo */}
            <div className="mx-auto">
              <img 
                src="/images/logo-betaye.jpg" 
                alt="BE-TAYE" 
                className="w-28 h-auto mx-auto"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                Data Intelligence
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                L'IA qui pilote vos décisions
              </p>
            </div>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm text-muted-foreground">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre.email@betaye.fr"
                    className="pl-10"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm text-muted-foreground">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full btn-primary bg-primary text-primary-foreground font-semibold h-11"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></span>
                    Connexion...
                  </span>
                ) : "Se connecter"}
              </Button>
              
              <div className="text-center mt-4">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
            </form>
            
            <p className="mt-6 text-center text-xs text-muted-foreground">
              Contactez votre administrateur pour obtenir un accès
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
